
import { useCallback } from "react";
import { fetchServices } from "@/data/services/serviceOperations";
import { supabase } from "@/integrations/supabase/client";
import { QUERY_VERSION } from "@/constants/serviceQueryConstants";
import { ServiceFetchOptions } from "@/types/serviceQueryTypes";
import { diagnoseConnectionIssues } from "@/utils/networkDiagnostics";
import { 
  logEntry,
  logExit,
  logQuery
} from '@/utils/enhancedLogging';

/**
 * Hook that provides service fetching functionality with better error handling and diagnostics
 */
export const useServiceFetcher = () => {
  /**
   * Function to fetch services with proper typing, enhanced error handling and diagnostics
   */
  const fetchServicePage = useCallback(async (
    page: number,
    searchQuery: string,
    activeSubcategoryId: number | null,
    selectedRegion: string,
    pageSize: number,
    regionData: any
  ) => {
    const entryId = logEntry('fetchServicePage', { 
      page, searchQuery, activeSubcategoryId, selectedRegion, pageSize,
      queryVersion: QUERY_VERSION
    });
    
    try {
      logQuery('fetchServices', { 
        page, searchQuery, activeSubcategoryId, selectedRegion, pageSize,
        queryVersion: QUERY_VERSION
      });
      
      // Start diagnostics timestamp
      const startTime = performance.now();
      
      // Add timeout for better network diagnostics
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Query timeout after 20s')), 20000)
      );
      
      // Race the query against a timeout
      const fetchPromise = fetchServices(
        searchQuery, 
        true, // publishedOnly 
        page, 
        pageSize, 
        activeSubcategoryId,
        selectedRegion
      );
      
      const fetchResult = await Promise.race([
        fetchPromise,
        timeoutPromise
      ]) as any;
      
      const { services, count, totalPages, error, isUsingFallback } = fetchResult;
      
      // Log timing information for performance analysis
      const fetchDuration = performance.now() - startTime;
      logQuery('fetchServices:complete', { 
        duration: `${fetchDuration.toFixed(2)}ms`,
        receivedCount: services?.length || 0
      });
      
      if (error) {
        // Enhanced error diagnostics
        logQuery('fetchServices:error', {
          code: error.code,
          message: error.message,
          details: error.details || 'No additional details',
          hint: error.hint
        });
        
        // If we encounter an error, run network diagnostics to help troubleshoot
        const diagnosticsPromise = diagnoseConnectionIssues().then(diagnostics => {
          logQuery('networkDiagnostics', diagnostics);
          return diagnostics;
        });
        
        // Don't wait for diagnostics to complete, just let it run in background
        // but throw the original error to maintain the error flow
        throw error;
      }
      
      if (!services || services.length === 0) {
        const queryResult = {
          services: [],
          totalCount: 0,
          currentPage: page,
          totalPages: 0,
          isUsingFallback
        };
        
        logExit('fetchServicePage', entryId, { 
          status: 'empty', 
          isUsingFallback 
        });
        
        return queryResult;
      }

      // Process the services with region data
      const processedServices = services.map(service => {
        // Set region currency and timezone data
        return {
          ...service,
          region_currency: regionData?.currency || 'Ksh',
          region_timezone: regionData?.timezone || 'Africa/Nairobi'
        };
      });
      
      // Log success metrics
      logQuery('fetchServices:success', {
        processedCount: processedServices.length,
        page,
        totalPages,
        isUsingFallback
      });
      
      const queryResult = {
        services: processedServices,
        totalCount: count,
        currentPage: page,
        totalPages,
        isUsingFallback
      };
      
      logExit('fetchServicePage', entryId, {
        status: 'success',
        count: processedServices.length
      });
      
      return queryResult;
    } catch (error: any) {
      // Enhanced error logging with custom error IDs for tracing
      const errorId = `err-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
      
      logQuery('fetchServices:fatal', {
        errorId,
        message: error?.message,
        code: error?.code,
        stack: error?.stack?.substring(0, 300) || 'No stack trace',
        params: { page, searchQuery, activeSubcategoryId, selectedRegion, pageSize }
      });
      
      // Run connection diagnostics on error to help troubleshoot network issues
      try {
        diagnoseConnectionIssues().then(diagnostics => {
          logQuery(`connectionDiagnostics:${errorId}`, diagnostics);
        });
      } catch (diagError) {
        console.error('Error running diagnostics:', diagError);
      }
      
      logExit('fetchServicePage', entryId, {
        status: 'error',
        errorId,
        message: error?.message
      });
      
      throw error;
    }
  }, []);

  return { fetchServicePage };
};
